// src/Studio/canvas/utils/keyframeRenderer.ts
// Helper utilities for applying keyframe animations during canvas rendering

import type { Element, MotionPath } from "../../types";
import { getAnimatedValues } from "../../engine/keyframeEngine";
import { precomputePath, evaluatePath, type PrecomputedPath } from "../../engine/motionPathEngine";
import { computeFollowTransform, type FollowState } from "../../engine/followEngine";
import { evaluateMotionEffects, type MotionEffect } from "../../engine/motionCompositionEngine";

// Cache for precomputed paths to avoid recalculating every frame
const pathCache = new WeakMap<MotionPath, PrecomputedPath>();

function getPrecomputedPath(motionPath: MotionPath): PrecomputedPath {
  let cached = pathCache.get(motionPath);
  // `motionPath` objects are replaced on edits (new reference), so a WeakMap cache
  // is sufficient without additional invalidation heuristics.
  if (!cached) {
    cached = precomputePath(motionPath);
    pathCache.set(motionPath, cached);
  }
  return cached;
}

type AnimatedProps = {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
};

type KeyframeContext = {
  elementsById?: Map<string, Element>;
  /** Clips array for looking up element's current timeline position */
  clips?: Array<{ elementId: string; start: number; end: number; [key: string]: any }>;
  /** Canvas dimensions for percentage-based motion calculations */
  canvasWidth?: number;
  canvasHeight?: number;
};

// Follow runtime state (for smoothing). Keyed by element id.
const followStateCache = new Map<string, { state: FollowState; lastPlayhead: number }>();

/**
 * Apply keyframe animations to element properties
 * Returns modified properties object for Konva rendering
 */
export function applyKeyframeAnimations(
  element: Element,
  playhead: number, // Current timeline playhead position in seconds
  baseProps: AnimatedProps,
  context?: KeyframeContext
): AnimatedProps {
  return applyKeyframeAnimationsInternal(element, playhead, baseProps, context, { disableFollow: false });
}

function applyKeyframeAnimationsInternal(
  element: Element,
  playhead: number,
  baseProps: AnimatedProps,
  context: KeyframeContext | undefined,
  opts: { disableFollow: boolean }
): AnimatedProps {
  let result: AnimatedProps = { ...baseProps };

  // DYNAMIC CLIP LOOKUP: Find element's current clip position from clips array
  // This ensures animation follows the clip when it's moved on the timeline
  let clipStart: number | undefined;
  let clipEnd: number | undefined;

  if (context?.clips && Array.isArray(context.clips)) {
    const elementClip = context.clips.find((c) => c.elementId === element.id);
    if (elementClip) {
      clipStart = elementClip.start;
      clipEnd = elementClip.end;
    }
  }

  // Fallback to stored values if clips not available (shouldn't happen normally)
  if (clipStart === undefined) {
    clipStart = (element as any).motionClipStart;
  }

  // Compute local time: tLocal = playhead - clipStart
  const hasValidClipStart = typeof clipStart === 'number' && Number.isFinite(clipStart);
  const effectiveClipStart = hasValidClipStart ? (clipStart as number) : 0;
  const tLocal = playhead - effectiveClipStart;

  // NEW: Composable motion effects system
  // If element has motionEffects array, use the composition engine
  const motionEffects = (element as any).motionEffects as MotionEffect[] | undefined;
  if (motionEffects && motionEffects.length > 0) {
    // Only apply motion if we're at or after the element's start time
    if (!hasValidClipStart || tLocal >= 0) {
      const composed = evaluateMotionEffects(
        motionEffects,
        tLocal,
        element,
        baseProps,
        {
          canvasWidth: context?.canvasWidth ?? 1920,
          canvasHeight: context?.canvasHeight ?? 1080,
          globalTime: playhead,
        }
      );

      result = {
        x: Number.isFinite(composed.x) ? composed.x : baseProps.x,
        y: Number.isFinite(composed.y) ? composed.y : baseProps.y,
        scaleX: Number.isFinite(composed.scaleX) ? composed.scaleX : baseProps.scaleX,
        scaleY: Number.isFinite(composed.scaleY) ? composed.scaleY : baseProps.scaleY,
        rotation: Number.isFinite(composed.rotation) ? composed.rotation : baseProps.rotation,
        opacity: Number.isFinite(composed.opacity) ? composed.opacity : baseProps.opacity,
      };
    }
  }
  // LEGACY: Keyframe tracks system (backward compatibility)
  else if (element.keyframeTracks && element.keyframeTracks.length > 0) {
    // Only apply motion animation if we're at or after the element's start time
    if (!hasValidClipStart || tLocal >= 0) {
      const animatedValues = getAnimatedValues(element.keyframeTracks, tLocal, element);

      // Helper to ensure value is a valid finite number (guards against NaN/Infinity)
      const safeVal = (val: number | undefined, fallback: number): number => {
        return typeof val === 'number' && Number.isFinite(val) ? val : fallback;
      };

      result = {
        x: safeVal(animatedValues.x, result.x),
        y: safeVal(animatedValues.y, result.y),
        scaleX: safeVal(animatedValues.scaleX, result.scaleX),
        scaleY: safeVal(animatedValues.scaleY, result.scaleY),
        rotation: safeVal(animatedValues.rotation, result.rotation),
        opacity: safeVal(animatedValues.opacity, result.opacity),
      };
    }
    // If tLocal < 0, we keep baseProps (no motion applied before element appears)
  }

  // Apply motion path if present
  if (element.motionPath && element.motionPath.points.length >= 2) {
    const pathResult = applyMotionPath(element, playhead, result, {
      // If the element is in Look-At follow mode, auto-rotate is usually not desired.
      // Keep rotation driven by Look-At instead.
      disableAutoRotate: element.followTarget?.mode === "lookAt",
    });
    result = pathResult;
  }

  // Apply follow target (runtime-derived; does not write keyframes)
  if (!opts.disableFollow && element.followTarget && context?.elementsById) {
    const followTarget = element.followTarget;
    const targetElement = context.elementsById.get(followTarget.targetElementId);
    if (targetElement) {
      const delay = Number.isFinite(followTarget.delay as number) ? Math.max(0, Number(followTarget.delay)) : 0;
      const delayedTime = Math.max(0, playhead - delay);

      const targetBase: AnimatedProps = {
        x: targetElement.x,
        y: targetElement.y,
        scaleX: targetElement.scaleX ?? 1,
        scaleY: targetElement.scaleY ?? 1,
        rotation: targetElement.rotation ?? 0,
        opacity: targetElement.opacity ?? 1,
      };
      const targetAnimated = applyKeyframeAnimationsInternal(
        targetElement,
        delayedTime,
        targetBase,
        context,
        { disableFollow: true }
      );

      // Use an animated target clone so follow uses the actual playback position.
      const targetAnimatedElement = {
        ...targetElement,
        x: targetAnimated.x,
        y: targetAnimated.y,
        rotation: targetAnimated.rotation,
        scaleX: targetAnimated.scaleX,
        scaleY: targetAnimated.scaleY,
        opacity: targetAnimated.opacity,
      } as Element;

      // Use the element's current animated props as the baseline.
      const elementAnimatedElement = {
        ...element,
        x: result.x,
        y: result.y,
        rotation: result.rotation,
        scaleX: result.scaleX,
        scaleY: result.scaleY,
        opacity: result.opacity,
      } as Element;

      const cached = followStateCache.get(element.id);
      const prevState =
        cached && Number.isFinite(cached.lastPlayhead) && playhead >= cached.lastPlayhead
          ? cached.state
          : undefined;

      const rotationOffsetDeg = followTarget.mode === "lookAt" ? result.rotation : undefined;

      const { result: followResult, state: nextState } = computeFollowTransform(
        elementAnimatedElement,
        followTarget,
        targetAnimatedElement,
        playhead,
        prevState,
        { rotationOffsetDeg }
      );

      followStateCache.set(element.id, { state: nextState, lastPlayhead: playhead });

      // Guard against NaN from follow calculations
      const safeFollow = (val: number | undefined, fallback: number): number => {
        return typeof val === 'number' && Number.isFinite(val) ? val : fallback;
      };

      result = {
        ...result,
        x: safeFollow(followResult.x, result.x),
        y: safeFollow(followResult.y, result.y),
        rotation: safeFollow(followResult.rotation, result.rotation),
      };
    }
  }

  return result;
}

/**
 * Apply motion path to element transforms.
 * Motion path provides position offset; rotation is additive if autoRotate is enabled.
 */
export function applyMotionPath(
  element: Element,
  playhead: number,
  currentProps: AnimatedProps,
  opts?: { disableAutoRotate?: boolean }
): AnimatedProps {
  const motionPath = element.motionPath;
  if (!motionPath || motionPath.points.length < 2) {
    return currentProps;
  }

  // Get progress from keyframe tracks (look for a 'pathProgress' track)
  // Progress is 0-1 representing position along the path
  let progress = 0;

  if (element.keyframeTracks) {
    const progressTrack = element.keyframeTracks.find(
      (t) => t.property === "pathProgress" as any
    );
    if (progressTrack) {
      const values = getAnimatedValues([progressTrack], playhead, element);
      progress = (values as any).pathProgress ?? 0;
    }
  }

  // Clamp progress to [0, 1] (keyframes should drive this).
  progress = Math.max(0, Math.min(1, progress));

  // Evaluate path at current progress
  const precomputed = getPrecomputedPath(motionPath);
  const pathPos = evaluatePath(precomputed, progress);

  const firstPoint = motionPath.points[0];
  // Additive offset (default) to avoid breaking existing x/y keyframes.
  // Guard against NaN in path calculations
  const dx = Number.isFinite(pathPos.x) && Number.isFinite(firstPoint.x) ? pathPos.x - firstPoint.x : 0;
  const dy = Number.isFinite(pathPos.y) && Number.isFinite(firstPoint.y) ? pathPos.y - firstPoint.y : 0;
  const newX = Number.isFinite(currentProps.x + dx) ? currentProps.x + dx : currentProps.x;
  const newY = Number.isFinite(currentProps.y + dy) ? currentProps.y + dy : currentProps.y;

  // Apply auto-rotate if enabled
  let newRotation = currentProps.rotation;
  if (motionPath.autoRotate && !opts?.disableAutoRotate) {
    // Convert tangent angle from radians to degrees and add to the element's current rotation
    const tangentDegrees = Number.isFinite(pathPos.tangentAngle) ? (pathPos.tangentAngle * 180) / Math.PI : 0;
    const calculated = currentProps.rotation + tangentDegrees;
    newRotation = Number.isFinite(calculated) ? calculated : currentProps.rotation;
  }

  return {
    ...currentProps,
    x: newX,
    y: newY,
    rotation: newRotation,
  };
}

